import { UserService } from '../../../services/user/user.service';
import { LocalHistoryService } from '../../../services/local-history.service';
import { BreakpointsDetectorService } from '../../../services/breakpoints-detector.service';
import { Component, inject, Input, OnInit } from '@angular/core';
import { GameMode, GamesLauncherService } from '../../../services/games/games-launcher.service';
import { DefaultImageDirective, DefaultImageType } from '../../directives/default-image.directive';
import { FavoriteGamesService } from '../../../services/games/favorite-games.service';
import { LanguageService } from '../../../services/language.service';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { LocalstorageService } from '../../../services/localstorage.service';
import { CommonDataService } from '../../../services/common-data.service';
import { UserPaymentModeService } from '../../../services/user/user-payment-mode.service';
import { of } from 'rxjs';
import { delay, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DecimalPipe, NgClass } from '@angular/common';
import { GoogleTagManagerService } from '../../../services/google-tag-manager.service';
import { ThemeService } from '../../../services/theme.service';
import { SeoService } from '../../../services/seo.service';
import { TranslatePipe } from '../../../services/translation/translate.pipe';
import { TextGradientComponent } from '../text-gradient/text-gradient.component';
import { ClassLoadedDirective } from '../../directives/class-loaded.directive';
import { GameFilterService } from '../../../services/games/game-filter.service';

@UntilDestroy()
@Component({
  selector: 'app-game-preview',
  templateUrl: './game-preview.component.html',
  styleUrls: ['./game-preview.component.scss'],
  standalone: true,
  imports: [NgClass, DefaultImageDirective, ClassLoadedDirective, TextGradientComponent, TranslatePipe],
})
export class GamePreviewComponent implements OnInit {

  /**
   * Current game
   */
  @Input() game: any = {};
  @Input() isShowGameInfo: boolean = true;

  public isToggleOverlay = false;

  public jackpotValue: string | number;
  public currency: string;

  /**
   * Game modes
   */
  modes = {
    real: GameMode.REAL,
    demo: GameMode.DEMO,
  };

  public lang = inject(LanguageService);
  public user = inject(UserService);
  public favourite = inject(FavoriteGamesService);
  public themeService = inject(ThemeService);
  public seo = inject(SeoService);
  private _gameFilters = inject(GameFilterService);
  private _local = inject(LocalHistoryService);
  private _router = inject(Router);
  private _breakPoint = inject(BreakpointsDetectorService);
  private _launcher = inject(GamesLauncherService);
  private _localStorage = inject(LocalstorageService);
  private _common = inject(CommonDataService);
  private _paymentMode = inject(UserPaymentModeService);
  private _number = inject(DecimalPipe);
  private _gtm = inject(GoogleTagManagerService);

  get defaultImageType() {
    return DefaultImageType;
  }

  /**
   * Apply game url for seo
   */
  get gameUrl() {
    return `${environment.ss_host}/${this.lang.current}/play/${this.game.slug}`;
  }

  ngOnInit() {
    this.favourite.setFavoriteStatus(this.game);
    this._resolveJackpotValue();
  }

  /**
   * Redirect to game
   */
  async openGame(mode: GameMode, event: Event) {

    event.stopPropagation();

    if (this._gameFilters.state().isShowSearchBar) {
      this._gameFilters.toggleSearchPopup();
    }

    switch (true) {
      case this.user.auth && mode === GameMode.REAL:
        await this._launchGame(GameMode.REAL);
        break;
      case !this.user.auth && mode === GameMode.DEMO:
        await this._launchGame(GameMode.DEMO);
        break;
      default:
        await this.user.authUser();
    }

  }

  /**
   * Toggle overlay for devices
   */
  toggleOverlay() {
    if (this._breakPoint.isTablet || this._breakPoint.isMobile) {
      this.isToggleOverlay = !this.isToggleOverlay;
    }
  }

  /**
   * Toggle favourite
   * @param game
   * @param event
   */
  toggleFavourite(game: any, event: Event) {
    event.stopPropagation();
    if (this._launcher.checkGameAvailability(game)) {
      this.favourite.toggleFavorite(game);
    }
  }

  /**
   * Check mode and redirect to game
   * @param mode
   * @private
   */
  private async _launchGame(mode: GameMode) {
    const gameUrl = `${this.lang.current}/play/${this.game.slug}`;
    this._local.addUrl('before-play', this._router.url);
    await this._router.navigateByUrl(gameUrl, {
      state: {
        game: this.game,
        mode,
      },
    });
  }

  /**
   * Resolve jackpot value
   * @private
   */
  private _resolveJackpotValue() {
    of(true).pipe(
      untilDestroyed(this),
      delay(500),
      tap(() => {
        this.currency = this.user.auth ? this.user.currentCurrency?.currency : (this._paymentMode.isModeCrypto ? 'BTC' : 'EUR');
        const jackpot = this.game.externalData.real?.[this.currency]?.jackpot;
        this.jackpotValue = jackpot ?
          this._number.transform(this._common.subunitsToUnits(jackpot, this.currency), this._resolveDigits())
          : null;
      }),
    ).subscribe();
  }

  /**
   * Resolve digits for transform pipe
   * @private
   */
  private _resolveDigits(): string {
    if (this._common.isCryptoCurrency(this.currency) && this.currency !== 'BTC') {
      return '0.4';
    } else if (this.currency === 'BTC') {
      return '0.8';
    } else {
      return '0.2';
    }
  }

  /**
   * Send game click event
   * @param game
   * @param mode
   * @private
   */
  private _sendGameClickEvent(mode: string) {
    const gameIdData = this._launcher.resolveGameId(this.game, mode === GameMode.DEMO);
    const gameId = gameIdData && gameIdData.id;
    if (gameId) {
      this._gtm.gameClickEvent(String(gameId));
    }
  }
}
